import { onMount, onCleanup } from 'solid-js';
import {
  type CorrectComponentType,
  customShadowlessElement,
  correctElementType,
} from '@brytdesigns/web-component-core/utils';
import { animate, type AnimationPlaybackControls } from 'motion';

type Props = {};

const TARGET_SELECTOR = '[data-gnav-indicator-target]';
const BAR_SELECTOR = '[data-gnav-indicator-bar]';

const REVEAL_DURATION = 0.15;
const MOVE_DURATION = 0.3;
const HIDE_DURATION = 0.2;
const MOVE_EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

const GnavIndicator: CorrectComponentType<Props> = (_props, { element }) => {
  let activeAnimation: AnimationPlaybackControls | null = null;
  let currentTarget: HTMLElement | null = null;
  let isVisible = false;

  function getBar() {
    return document.querySelector<HTMLElement>(BAR_SELECTOR);
  }

  function moveTo(target: HTMLElement) {
    const bar = getBar();
    if (!bar) return;
    const barParent = bar.offsetParent as HTMLElement | null;
    if (!barParent) return;

    const parentRect = barParent.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    const x = rect.left - parentRect.left;
    const width = rect.width;

    if (activeAnimation) activeAnimation.stop();

    if (!isVisible) {
      // Force motion's internal state for x/width to the new target via explicit
      // keyframes — setting inline styles directly would be overridden by motion's
      // tracked values on the next animation. Opacity fades in from 0.
      activeAnimation = animate(
        bar,
        { x: [x, x], width: [width, width], opacity: [0, 1] },
        { duration: REVEAL_DURATION, ease: 'easeOut' },
      );
      isVisible = true;
      return;
    }

    activeAnimation = animate(bar, { x, width, opacity: 1 }, { duration: MOVE_DURATION, ease: MOVE_EASE });
  }

  function hide() {
    const bar = getBar();
    if (!bar) return;
    if (activeAnimation) activeAnimation.stop();
    activeAnimation = animate(bar, { opacity: 0 }, { duration: HIDE_DURATION });
    currentTarget = null;
    isVisible = false;
  }

  function resolveFromElement(el: Element | null): HTMLElement | null {
    if (!el) return null;
    const li = el.closest('li');
    if (!li || !element.contains(li)) return null;
    return li.querySelector<HTMLElement>(TARGET_SELECTOR);
  }

  let pendingFrame: number | null = null;
  let lastPointer: { x: number; y: number } | null = null;

  function scheduleSampling() {
    if (pendingFrame !== null) return;
    pendingFrame = requestAnimationFrame(() => {
      pendingFrame = null;
      if (!lastPointer) return;
      const el = document.elementFromPoint(lastPointer.x, lastPointer.y);
      const target = resolveFromElement(el);
      if (!target || target === currentTarget) return;
      currentTarget = target;
      moveTo(target);
    });
  }

  function onPointerMove(event: PointerEvent) {
    lastPointer = { x: event.clientX, y: event.clientY };
    scheduleSampling();
  }

  function onFocusIn(event: FocusEvent) {
    const target = resolveFromElement(event.target as Element | null);
    if (!target || target === currentTarget) return;
    currentTarget = target;
    moveTo(target);
  }

  function onPointerLeave(event: Event) {
    if (event.currentTarget !== element) return;
    lastPointer = null;
    if (pendingFrame !== null) {
      cancelAnimationFrame(pendingFrame);
      pendingFrame = null;
    }
    hide();
  }

  function onFocusOut(event: FocusEvent) {
    const next = event.relatedTarget as Node | null;
    if (next && element.contains(next)) return;
    hide();
  }

  onMount(() => {
    element.addEventListener('pointermove', onPointerMove);
    element.addEventListener('focusin', onFocusIn);
    element.addEventListener('mouseleave', onPointerLeave);
    element.addEventListener('focusout', onFocusOut);
  });

  onCleanup(() => {
    element.removeEventListener('pointermove', onPointerMove);
    element.removeEventListener('focusin', onFocusIn);
    element.removeEventListener('mouseleave', onPointerLeave);
    element.removeEventListener('focusout', onFocusOut);
    if (pendingFrame !== null) cancelAnimationFrame(pendingFrame);
    if (activeAnimation) activeAnimation.stop();
  });
};

customShadowlessElement('gnav-indicator', {}, correctElementType(GnavIndicator));
